// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricConfig, BaseMetricEntity, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for Maintainers entity
 */
type MaintainersConfig = BaseMetricConfig;

/**
 * MaintainersEntity - Domain entity for maintainers business rules
 *
 * This entity encapsulates the business logic for calculating and determining
 * maintainer influence levels based on count of maintainers employed by the company.
 *
 * Business Rules:
 * - 0 maintainers = Silent (0 points) - Red color, (!) icon
 * - 1 or more maintainers = Leading (3 points) - Green color, thumbs up icon
 */
export class Maintainers extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'maintainers' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: MaintainersConfig = {
    metricType: 'maintainers',
    display: {
      displayName: 'Maintainers',
      chartType: 'bar' as ChartType,
      tooltip: {
        definition: 'Does our company have a maintainer?',
        scoringRules: [
          '0 maintainers = 0 points',
          '1 or more maintainers = 10 points',
          '0 points = <b>Silent</b>',
          '10 points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company does not employ any maintainers for this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Our company employs 1 or more maintainers for this project.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for maintainers
   */
  static getBottomSummary(value?: number, level?: ScoreLevel): MetricBottomSummary {
    const maintainerCount = value || 0;
    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();
    const bottomSummary = this.CONFIG.bottomSummary;
    const description = maintainerCount === 0 ? bottomSummary.zeroValue.description : bottomSummary.positiveValue.descriptionTemplate

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }
}
